import React from "react";
import logo from "../assets/ft-logo.svg";

const STEPS = [
  { key: "document-upload", label: "Document Upload", icon: "upload" },
  { key: "pending-data", label: "Pending Data Entry", icon: "data" },
  { key: "review-submit", label: "Review & Submit", icon: "review" },
];

const StepSidebar = ({ householdData, currentStep, className = "" }) => {
  if (!householdData) return null;

  const members = householdData.household_members || householdData.members || [];
  const primaryMember = members.find((m) => m.is_primary === true);
  let personalInfo = primaryMember?.personal_info || {};
  if (typeof personalInfo === "string") {
    try { personalInfo = JSON.parse(personalInfo); } catch { personalInfo = {}; }
  }
  const firstName = personalInfo.first_name || "";
  const lastName = personalInfo.last_name || "";
  const fullName = `${firstName} ${lastName}`.trim();
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();

  const completionPercentage = householdData.completion_percentage
    ? Math.floor(householdData.completion_percentage)
    : 0;

  const advisorDetails = householdData.advisor_details || {};
  const advisorFirstName = advisorDetails.first_name || "";
  const advisorLastName = advisorDetails.last_name || "";
  const advisorName = `${advisorFirstName} ${advisorLastName}`.trim();
  const advisorInitials = `${advisorFirstName.charAt(0)}${advisorLastName.charAt(0)}`.toUpperCase();
  const advisorEmail = advisorDetails.email || "";

  const currentStepIndex = STEPS.findIndex((s) => s.key === currentStep);

  const getStepStatus = (index) => {
    if (index < currentStepIndex) return "completed";
    if (index === currentStepIndex) return "active";
    return "future";
  };

  const renderStepIcon = (step, status) => {
    if (status === "completed") {
      return (
        <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    }

    const iconColor = status === "active" ? "text-white" : "text-gray-400";
    const bgColor = status === "active" ? "bg-emerald-500" : "bg-gray-200";

    const icons = {
      upload: (
        <svg className={`w-4 h-4 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      data: (
        <svg className={`w-4 h-4 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      review: (
        <svg className={`w-4 h-4 ${iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
    };

    return (
      <div className={`w-8 h-8 rounded-full ${bgColor} flex items-center justify-center flex-shrink-0`}>
        {icons[step.icon]}
      </div>
    );
  };

  return (
    <div className={`h-full min-h-0 flex flex-col bg-white w-64 border-r border-gray-200 flex-shrink-0 ${className}`}>
      <div className="p-6 flex-1">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <img src={logo} alt="FastTrackr" className="h-10 w-10" onError={(e) => { e.target.style.display = "none"; }} />
          <span className="text-s font-bold text-gray-900">FastTrackr AI</span>
        </div>

        {/* User Info */}
        <div className="flex flex-col items-center mb-10">
          <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-lg mb-2">
            {initials || "?"}
          </div>
          <p className="text-sm font-bold text-gray-900 truncate max-w-full">{fullName || "Client"}</p>
        </div>

        {/* Completion Status */}
        <div className="mb-10">
          <div className="flex justify-between items-center mb-1.5">
            <span className="text-sm font-medium text-gray-500">Completion Status</span>
            <span className="text-sm font-bold text-emerald-600">{completionPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-emerald-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
        </div>

        {/* Step Navigation */}
        <nav className="space-y-1">
          {STEPS.map((step, index) => {
            const status = getStepStatus(index);
            return (
              <div
                key={step.key}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  status === "active"
                    ? "bg-emerald-50 text-emerald-700"
                    : status === "completed"
                    ? "text-gray-700"
                    : "text-gray-400"
                }`}
              >
                {renderStepIcon(step, status)}
                <span className={`text-sm ${status === "active" ? "font-semibold" : "font-medium"}`}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Advisor Info */}
      <div className="mt-auto p-6 border-t border-gray-100">
        <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-3">
          Your Advisor
        </p>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-bold text-xs flex-shrink-0">
            {advisorInitials || "?"}
          </div>
          <div className="min-w-0">
            {advisorName && (
              <p className="text-sm font-semibold text-gray-900 truncate">{advisorName}</p>
            )}
            {advisorEmail && (
              <p className="text-xs text-gray-500 truncate">{advisorEmail}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StepSidebar;
